class CustomTestimonialsSlider extends HTMLElement {
  constructor() {
    super();
    this.onScroll = this.onScroll.bind(this);
    this.onPrev = this.onPrev.bind(this);
    this.onNext = this.onNext.bind(this);
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    this.viewport = this.querySelector('[data-viewport]');
    this.track = this.querySelector('[data-track]');
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.controls = this.querySelector('[data-controls]');
    this.pagination = this.querySelector('[data-pagination]');
    this.prevButton = this.querySelector('[data-prev]');
    this.nextButton = this.querySelector('[data-next]');
    this.pageButtons = [];
    this.currentPage = 0;
    this.pagePositions = [];

    if (!this.viewport || this.slides.length === 0) {
      return;
    }

    this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
    this.viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        this.onPrev();
      }
      if (event.key === 'ArrowRight') {
        this.onNext();
      }
    });

    this.prevButton && this.prevButton.addEventListener('click', this.onPrev);
    this.nextButton && this.nextButton.addEventListener('click', this.onNext);

    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this.viewport);

    window.addEventListener('resize', this.onResize, { passive: true });
    this.refresh();
  }

  disconnectedCallback() {
    this.viewport && this.viewport.removeEventListener('scroll', this.onScroll);
    this.prevButton && this.prevButton.removeEventListener('click', this.onPrev);
    this.nextButton && this.nextButton.removeEventListener('click', this.onNext);
    this.resizeObserver && this.resizeObserver.disconnect();
    window.removeEventListener('resize', this.onResize);
  }

  getSlidesPerView() {
    if (window.innerWidth >= 990) return 3;
    if (window.innerWidth >= 750) return 2;
    return 1;
  }

  refresh() {
    const slidesPerView = this.getSlidesPerView();
    const maxStartIndex = Math.max(1, this.slides.length - slidesPerView + 1);
    const maxScrollLeft = Math.max(0, this.track.scrollWidth - this.viewport.clientWidth);
    this.pagePositions = [];

    for (let index = 0; index < maxStartIndex; index += 1) {
      const nextPosition = Math.min(this.slides[index].offsetLeft, maxScrollLeft);

      if (this.pagePositions[this.pagePositions.length - 1] !== nextPosition) {
        this.pagePositions.push(nextPosition);
      }
    }

    this.currentPage = this.getClosestPageIndex();
    this.renderPagination();
    this.updateControls();
  }

  renderPagination() {
    if (!this.pagination) {
      return;
    }

    const pageCount = this.pagePositions.length;
    this.pagination.innerHTML = '';
    this.pageButtons = [];

    if (pageCount <= 1) {
      this.controls && (this.controls.hidden = true);
      return;
    }

    this.controls && (this.controls.hidden = false);

    this.pagePositions.forEach((_, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'testimonials-pagination-button';
      button.setAttribute('aria-label', `Go to testimonial page ${index + 1}`);
      button.addEventListener('click', () => this.scrollToPage(index));
      this.pagination.appendChild(button);
      this.pageButtons.push(button);
    });

    this.updatePagination();
  }

  updatePagination() {
    this.pageButtons.forEach((button, index) => {
      const isActive = index === this.currentPage;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  updateControls() {
    const maxIndex = this.pagePositions.length - 1;

    if (this.prevButton) {
      this.prevButton.disabled = this.currentPage <= 0;
    }

    if (this.nextButton) {
      this.nextButton.disabled = this.currentPage >= maxIndex;
    }

    this.updatePagination();
  }

  getClosestPageIndex() {
    if (!this.pagePositions.length) {
      return 0;
    }

    const currentLeft = this.viewport.scrollLeft;
    let closestIndex = 0;
    let closestDistance = Math.abs(this.pagePositions[0] - currentLeft);

    this.pagePositions.forEach((position, index) => {
      const distance = Math.abs(position - currentLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  scrollToPage(index) {
    if (!this.pagePositions.length) {
      return;
    }

    const safeIndex = Math.max(0, Math.min(index, this.pagePositions.length - 1));
    this.currentPage = safeIndex;
    this.viewport.scrollTo({
      left: this.pagePositions[safeIndex],
      behavior: 'smooth'
    });
    this.updateControls();
  }

  onPrev() {
    this.scrollToPage(this.currentPage - 1);
  }

  onNext() {
    this.scrollToPage(this.currentPage + 1);
  }

  onScroll() {
    if (this.scrollFrame) {
      return;
    }

    this.scrollFrame = window.requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.syncToScrollPosition();
    });
  }

  onResize() {
    this.refresh();
  }

  syncToScrollPosition() {
    const nextPage = this.getClosestPageIndex();

    if (nextPage !== this.currentPage) {
      this.currentPage = nextPage;
      this.updateControls();
    }
  }
}

if (!customElements.get('custom-testimonials-slider')) {
  customElements.define('custom-testimonials-slider', CustomTestimonialsSlider);
}
